import React, { useState } from 'react';
import { Trophy, Gift, Star, Clock, ChevronRight, Send, X } from 'lucide-react';
import { SubmissionModal } from '../components/SubmissionModal';
import { submitBountySolution } from '../services/bountyService';
import { useWallet } from '../hooks/useWallet';

interface Bounty {
  id: string;
  title: string;
  description: string;
  reward: number;
  points: number;
  deadline: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  status: 'Open' | 'In Progress' | 'Closed';
  requirements: string[];
  submissionGuidelines: string;
}

const CURRENT_BOUNTY: Bounty = {
  id: 'SWORD-002',
  title: '"How To dTAO?" An Easy-To-Understand Bittensor/dTAO Guide',
  description: 'Your task is to compose a straightforward yet comprehensive guide on the Bittensor ecosystem. Focus on clarity and accessibility, ensuring that newcomers to both AI and cryptocurrency can easily follow the content. In particular, your guide should cover:',
  reward: 500,
  points: 50,
  deadline: '2025-03-22',
  difficulty: 'Medium',
  status: 'Open',
  requirements: [
    'Bittensor as a decentralized platform for incentivized machine learning',
    'TAO, the primary token within the network',
    'The roles of Miners and Validators, including how they contribute to and secure the system',
    'Staking and emissions, emphasizing how they function and why they matter',
    'dTAO and its place in the broader ecosystem',
    'Alpha tokens and Subnets, explaining how specialized tasks or models are organized',
    'Practical steps to acquire Subnet Alpha tokens, detailing wallet creation and the bridging or conversion process',
    '🚨YOU MUST INCLUDE YOUR BITTENSOR WALLET ADDRESS IN YOUR SUBMISSION🚨'
  ],
  submissionGuidelines: 'Submit your guide in the box below. Get as creative as possible. Links to google docs or PDFs are also acceptable. Remember, this is supposed to be an easy-to-follow guide that normies should be able to understand. Good luck Warriors'
};

const getDifficultyColor = (difficulty: Bounty['difficulty']) => {
  switch (difficulty) {
    case 'Easy':
      return 'text-green-400';
    case 'Medium':
      return 'text-yellow-400';
    case 'Hard':
      return 'text-red-400';
    default:
      return 'text-gray-400';
  }
};

export default function BountyBoard() {
  const { address, isConnected, userData, refreshUserData } = useWallet();
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);

  const handleSubmitSolution = async (solution: string) => {
    if (!isConnected || !address || !userData) {
      throw new Error('Please connect your wallet to submit a solution');
    }

    await submitBountySolution({
      bountyId: CURRENT_BOUNTY.id,
      walletAddress: address,
      username: userData.username,
      solution,
      timestamp: new Date().toISOString()
    });

    // Refresh user data after successful submission
    await refreshUserData();
  };

  return (
    <div className="space-y-6">
      <div className="bg-black/40 backdrop-blur-sm rounded-xl shadow-2xl p-4 md:p-8 border border-white/10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Trophy className="w-8 h-8 text-red-600" />
            <h1 className="text-2xl md:text-3xl font-bold">Active Bounty</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 bg-black/20 rounded-lg px-4 py-2">
              <Gift className="w-5 h-5 text-red-600" />
              <span className="text-lg font-semibold">Reward: {CURRENT_BOUNTY.reward} USDC</span>
            </div>
          </div>
        </div>

        <div className="bg-black/20 backdrop-blur-sm rounded-lg p-6 border border-white/5">
          <div className="flex justify-between items-start mb-6">
            <div className="space-y-2">
              <h2 className="text-2xl font-bold">{CURRENT_BOUNTY.title}</h2>
              <div className="flex items-center space-x-4">
                <span className={`text-sm font-medium ${getDifficultyColor(CURRENT_BOUNTY.difficulty)}`}>
                  {CURRENT_BOUNTY.difficulty}
                </span>
                <span className="text-sm text-gray-400">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Due {new Date(CURRENT_BOUNTY.deadline).toLocaleDateString()}
                </span>
                <span className="text-sm text-green-400 font-medium">
                  {CURRENT_BOUNTY.status}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center text-yellow-400 bg-yellow-400/10 px-3 py-1 rounded-lg">
                <Star className="w-4 h-4 mr-1" />
                <span>{CURRENT_BOUNTY.points} Points</span>
              </div>
            </div>
          </div>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-2">Description</h3>
              <p className="text-gray-300 leading-relaxed">{CURRENT_BOUNTY.description}</p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-2">Requirements</h3>
              <ul className="list-none space-y-2">
                {CURRENT_BOUNTY.requirements.map((req, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-400 mr-2 text-lg leading-7">•</span>
                    <span className="text-gray-300">{req}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-2">Submission Guidelines</h3>
              <p className="text-gray-300 leading-relaxed">{CURRENT_BOUNTY.submissionGuidelines}</p>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-white/5 flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-400">Bounty ID: {CURRENT_BOUNTY.id}</span>
            </div>
            <button 
              onClick={() => setIsSubmissionModalOpen(true)}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <span>Submit Solution</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <SubmissionModal
        isOpen={isSubmissionModalOpen}
        onClose={() => setIsSubmissionModalOpen(false)}
        onSubmit={handleSubmitSolution}
      />
    </div>
  );
}